import React from 'react';
import * as R from 'ramda';
import YesNo from 'Components/YesNo';
import { capitalize, nl2p } from '~/utils';
import dayjs from 'dayjs';
import { Alert } from 'Components/nui';
import { useStoreState } from 'easy-peasy';
import { FormattedMessage, FormattedDate, FormattedTime } from 'react-intl';
import FNumber from 'Components/FNumber';
import { getDecimalCount, useLabels } from '~/utils';
import { Trade } from '~/models';

export default ({ record, bid, offer }) => {
  const label = useLabels(record);

  const bidOrPurchaseType = record.type === 'bid' || record.type === 'purchase';
  const solution = useStoreState((state) => state.auth.solution);
  const hasFlatFee = solution?.exportprice?.flatfee;

  const attributes = R.propOr([], ['attributes'], solution);
  let data = [];

  if (record.productionmonth) {
    data.push([
      label('productionmonth', 'Production month', record),
      record.productionmonth.display,
      'reversed',
      'productionmonth',
    ]);
  }

  attributes.forEach((item) => {
    if (R.pathOr(0, ['attributes', item.name, 'length'], record) > 0) {
      data.push([
        label(item.name, capitalize(item.name), record),
        record.attributes[item.name].map((x) => x.name).join(', '),
        'reversed',
        item.name,
      ]);
    }
  });

  if ('anonymous' in record) {
    data.push([
      <FormattedMessage
        id="extradetails-field-anonymous-label"
        description="Label for 'Anonymous' field on Extra details"
        defaultMessage="Anonymous"
      />,
      <YesNo yes={record.anonymous} />,
      'reversed',
      'anonymous',
    ]);
  }

  if (record?.brand && record.brand.length > 0) {
    data.push([
      label('brands', 'Brands', record),
      record.brand.map((x) => x.desc).join(', '),
      'stacked',
      'brands',
    ]);
  }

  if (record?.expiry) {
    data.push([
      label('expiry', 'Valid till', record),
      <>
        <FormattedDate
          value={dayjs(record.expiry).toDate()}
          day="numeric"
          month="long"
          year="numeric"
        />{' '}
        <FormattedTime
          value={dayjs(record.expiry).toDate()}
          hour="2-digit"
          minute="2-digit"
          hour12={false}
        />
      </>,
      'reversed',
      'valid-till',
    ]);
  }

  if (!R.isNil(record.exportdocs)) {
    data.push([
      label('exportdocs', 'Export docs', record),
      <YesNo yes={record.exportdocs} notRequired={bidOrPurchaseType} />,
      'reversed',
      'exportdocs',
    ]);
  }

  if (record?.exportable && record?.exportcountry) {
    data.push([
      label('exportcountry', 'Destination', record),
      record.exportcountry.name,
      'reversed',
      'exportcountry',
    ]);
  }

  if ('freightavailable' in record) {
    data.push([
      label('freightavailable', 'Additional freight', record),
      <YesNo yes={record.freightavailable} notRequired={bidOrPurchaseType} />,
      'reversed',
      'freightavailable',
    ]);
  }

  if (!R.isNil(record.exportable)) {
    data.push([
      label('exportable', 'Exportable', record),
      <YesNo
        yes={record.exportable}
        notRequired={bidOrPurchaseType && !hasFlatFee}
      />,
      'reversed',
      'exportable',
    ]);
  }

  if ('docs' in record) {
    if ('gfsi' in record.docs) {
      data.push([
        label('gfsi', 'GFSI certification', record),
        <YesNo
          yes={record.docs.gfsi === 'yes'}
          notRequired={bidOrPurchaseType}
        />,
        'reversed',
        'gfsi',
      ]);
    }
    if ('export' in record.docs) {
      data.push([
        label('export', 'Export documents', record),
        <YesNo
          yes={record.docs.export === 'yes'}
          notRequired={bidOrPurchaseType}
        />,
        'reversed',
        'export',
      ]);
    }
    if ('halal' in record.docs) {
      data.push([
        label('halal', 'Halal documents', record),
        <YesNo
          yes={record.docs.halal === 'yes'}
          notRequired={bidOrPurchaseType}
        />,
        'reversed',
        'halal',
      ]);
    }
    if ('kosher' in record.docs) {
      data.push([
        label('kosher', 'Kosher documents', record),
        <YesNo
          yes={record.docs.kosher === 'yes'}
          notRequired={bidOrPurchaseType}
        />,
        'reversed',
        'kosher',
      ]);
    }
  }

  if ('callofftime' in record) {
    data.push([
      label('callofftime', 'Call off times', record),
      nl2p(record.callofftime) || 'n/a',
      'reversed',
      'callofftime',
    ]);
  }

  if (record?.destinations?.length)
    data.push([
      label('destinations', 'Destination', record),
      record.destinations.map((item) => item.name).join(', '),
      'reversed',
      'destinations',
    ]);

  if ('origins' in record)
    data.push([
      label('origins', 'Origin', record),
      record.origins.map((item) => item.name).join(', '),
      'reversed',
      'origins',
    ]);

  if (record?.locations?.length)
    data.push([
      label('locations', 'Location', record),
      record.locations.map((item) => item.name).join(', '),
      'reversed',
      'locations',
    ]);

  if (record?.incoterms?.length)
    data.push([
      label('incoterms', 'Incoterm', record),
      record.incoterms.join(', '),
      'reversed',
      'incoterms',
    ]);

  if ('heattreatedpallets' in record) {
    data.push([
      label('pallets-required', 'Heat-treated pallets required', record),
      <YesNo yes={record.heattreatedpallets} notRequired={bidOrPurchaseType} />,
      'reversed',
      'pallets-required',
    ]);
  }

  if (record?.heattreatedpallets && record instanceof Trade) {
    const tradeVolume = record.volume.val;
    data.push([
      label('pallets-value', 'Heat-treated pallets summary', record),
      <span>
        <FNumber
          value={parseFloat(solution.exportprice.heattreatedfee)}
          decimalCount={getDecimalCount(
            parseFloat(solution.exportprice.heattreatedfee)
          )}
          unit={solution.currency}
        />{' '}
        &times;{' '}
        <FNumber
          value={tradeVolume}
          decimalCount={getDecimalCount(tradeVolume)}
        />{' '}
        ={' '}
        <FNumber
          value={parseFloat(solution.exportprice.heattreatedfee) * tradeVolume}
          decimalCount={2}
          unit={solution.currency}
        />
      </span>,
      'reversed',
      'pallets-value',
    ]);
  }

  if (record?.age && record.age.desc)
    data.push([
      label('age', 'Age', record),
      record.age.desc,
      'reversed',
      'age',
    ]);

  if (record?.productiondate)
    data.push([
      label('productiondate', 'Production date', record),
      <FormattedDate
        value={dayjs(record.productiondate).toDate()}
        day="numeric"
        month="long"
        year="numeric"
      />,
      'reversed',
      'productiondate',
    ]);

  if (record?.declinereason)
    data.push([
      label('declinereason', 'Decline reason', record),
      <span className="icon-block">{record.declinereason}</span>,
      'stacked',
      'declinereason',
    ]);

  if (record?.reference)
    data.push([
      label('reference', 'Reference', record),
      record.reference,
      'reversed',
      'reference',
    ]);

  const brands = R.pathOr([], ['brand', 'trade'], record);
  if (brands.length > 0) {
    data.push([
      label('brands', 'Brands', record),
      brands.map((item) => item.desc).join(', '),
      'stacked',
      'brands',
    ]);
  }

  if (bid && bid?.note) {
    data.push([
      label('note', 'Buyer comment', record),
      nl2p(bid.note),
      'stacked',
      'note',
    ]);
  }

  if (offer && offer?.note) {
    data.push([
      label('note', 'Seller comment', record),
      nl2p(offer.note),
      'stacked',
      'note',
    ]);
  }

  if (data.length === 0) return null;

  return (
    <>
      <h2>
        <FormattedMessage
          id="extradetails-header"
          description="Header for Extra details"
          defaultMessage="Extra details"
        />
      </h2>

      <ul className="trade-details-list loading-details">
        {data.length === 0 ? (
          <li>
            <Alert hasicon>
              <div className="align-center">
                <FormattedMessage
                  id="extradetails-header-empty"
                  description="Header for empty Extra details"
                  defaultMessage="No extra details"
                />
              </div>
            </Alert>
          </li>
        ) : (
          data.map(([message, value, className, key]) => (
            <li key={key} className={className}>
              <strong>{message}</strong>
              <span className={`item-${key}`}>{value}</span>
            </li>
          ))
        )}
      </ul>
    </>
  );
};
